import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/ui/Navbar";
import Footer from "../../components/ui/Footer";
import { authApi } from "../../services/api";

const STEPS = [
  "Enter the email address linked to your Astudent account.",
  "Check your inbox for a secure password reset link.",
  "Follow the link to set a new password and log back in.",
];

const inputClass = (hasError) =>
  `w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:outline-none focus:ring-2 ${
    hasError
      ? "border-red-300 focus:border-red-400 focus:ring-red-100"
      : "border-slate-200 focus:border-[#0BBF64] focus:ring-[#0BBF64]/20"
  }`;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resetLink, setResetLink] = useState("");

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validate = () => {
    if (!email.trim()) {
      setError("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Invalid email format");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);
    setError("");
    setSuccess("");
    setResetLink("");

    try {
      const response = await authApi.requestPasswordReset({ email });

      if (response.data && response.data.message === "successful") {
        const link = response.data.data;
        setResetLink(link);
        setSuccess(
          "Password reset link has been sent to your email. Check your inbox."
        );
      } else {
        setError(
          response.data?.message ||
            "Failed to send reset link. Please try again."
        );
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to send reset link. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-slate-900">
      <Navbar />

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10 lg:py-16">
          <div className="grid gap-8 lg:grid-cols-2 lg:gap-12 lg:items-stretch">
            {/* Marketing panel */}
            <section className="relative order-2 overflow-hidden rounded-2xl lg:order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900" />
              <div
                aria-hidden
                className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:2.5rem_2.5rem]"
              />
              <div
                aria-hidden
                className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[#0BBF64]/20 blur-3xl"
              />

              <div className="relative flex h-full flex-col justify-center p-8 sm:p-10 lg:p-12">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
                  Account recovery
                </p>
                <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
                  Reset your password securely
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">
                  We&apos;ll send a one-time link to your email so you can set a
                  new password and get back to your projects.
                </p>

                <ol className="mt-8 space-y-4">
                  {STEPS.map((text, i) => (
                    <li key={text} className="flex items-start gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-xs font-bold text-[#0BBF64]">
                        {i + 1}
                      </span>
                      <span className="text-sm leading-relaxed text-slate-200">
                        {text}
                      </span>
                    </li>
                  ))}
                </ol>

                <p className="mt-8 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-xs leading-relaxed text-slate-400 backdrop-blur-sm">
                  Remember your password?{" "}
                  <Link
                    to="/login"
                    className="font-semibold text-emerald-300 hover:text-emerald-200"
                  >
                    Back to log in
                  </Link>
                </p>
              </div>
            </section>

            {/* Form panel */}
            <section className="order-1 flex flex-col justify-center lg:order-2">
              <div className="mb-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-[#0BBF64]">
                  Forgot password
                </p>
                <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">
                  Recover your account
                </h1>
              </div>

              <p className="mb-6 text-sm text-slate-600">
                Enter the email you used to sign up. We&apos;ll send you a link
                to reset your password.
              </p>

              {error && (
                <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                  {success}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-sm font-medium text-slate-700"
                  >
                    Email address
                    <span className="text-[#0BBF64]"> *</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={handleChange}
                    className={inputClass(!!error)}
                    placeholder="you@example.com"
                  />
                </div>

                {resetLink && (
                  <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
                    <p className="mb-2 font-semibold">Reset link (for testing):</p>
                    <a
                      href={resetLink}
                      className="break-all font-medium text-sky-700 underline hover:text-sky-900"
                    >
                      {resetLink}
                    </a>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center rounded-xl bg-[#0BBF64] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:bg-[#0aa459] hover:shadow-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Sending link…" : "Send reset link"}
                </button>

                <p className="text-center text-sm text-slate-600">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1.5 font-semibold text-[#0BBF64] hover:text-[#0aa459]"
                  >
                    <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Back to log in
                  </Link>
                </p>
              </form>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ForgotPassword;
